import * as React from "react";
import { useState } from "react";
import { KatDateRangePicker, KatDropdown } from "@amzn/katal-react";
import {
    CASE_TYPE_OPTION,
    CASE_TYPE_TO_CASE_SUB_TYPE_MAP,
    NA_COUNTRY_OPTION,
    REGION_COUNTRY_CONFIG,
    REVIEW_TYPE_OPTION,
    STATUS_OPTION,
    TRANSPORTER_TYPE_OPTION
} from "src/app/config/DropDownConfig";
import { DropDownOption } from "src/app/model/DropDownOption";
import { QueryCondition } from "src/app/model/QueryCondition";
import { FilterOptionsPayload } from "src/app/api/model/response/FilterOptionsResponse";
import { Region } from "src/app/constant/Enums";
import { Validator } from "src/app/util/Validator";

/**
 * Shared, controlled filter bar for case/review querying.
 *
 * This is the single source of truth for the "case filters" UI. It renders the seven
 * controls (Reference SubType, Case Type, Case SubType, Country, Review Status, Review Type,
 * Date Range) and owns the two pieces of derived UI behavior:
 *   - dependent Case Type -> Case SubType (SubType resets to the first option of the new Type),
 *   - the 90-day (configurable) date-range validation.
 *
 * It is fully controlled: the parent owns the {@link QueryCondition} value and receives the
 * next value via {@link FilterBarProps.onChange}. It does NOT fetch data or render action
 * buttons (Search/Download/Reset) — each page composes those around it and maps the value to
 * its own request shape (ReviewExport -> ListReviewRequest, CaseAudit -> SearchCaseRequest).
 *
 * Consumers: page/ReviewExport.tsx and page/CaseAudit.tsx.
 */

const DEFAULT_DATE_RANGE_LIMIT_DAYS = 90;

/** Sentinel value for the "All" (no filter) dropdown option. Non-empty so KatDropdown
 *  actually selects and displays it (an empty value shows the placeholder instead). */
export const ALL_VALUE = 'ALL';

// Default filter values — identical to the review/export page.
export const DEFAULT_REFERENCE_SUB_TYPE = 'DP';
export const DEFAULT_CASE_TYPE = 'Abuse';
export const DEFAULT_CASE_SUB_TYPE = 'OPF';
export const DEFAULT_REVIEW_STATUS = 'PENDING';
export const DEFAULT_REVIEW_TYPE = 'PRIMARY_AUDIT';
const START_OFFSET_DAYS = 7;
const END_OFFSET_DAYS = 0;

/** Region-aware country options, resolved from the current URL (na/eu/fe), else NA. */
export function resolveCountryOptions(): DropDownOption[] {
    const url = window.location.href;
    if (url.indexOf(Region.NA) !== -1) return REGION_COUNTRY_CONFIG.get(Region.NA) || NA_COUNTRY_OPTION;
    if (url.indexOf(Region.EU) !== -1) return REGION_COUNTRY_CONFIG.get(Region.EU) || [];
    if (url.indexOf(Region.FE) !== -1) return REGION_COUNTRY_CONFIG.get(Region.FE) || [];
    return NA_COUNTRY_OPTION;
}

/** Default date window: [today - 7 days, today] (ISO strings). */
export function defaultDateRange(): { startDate: string; endDate: string } {
    const now = new Date();
    const start = new Date(now);
    start.setDate(now.getDate() - START_OFFSET_DAYS);
    const end = new Date(now);
    end.setDate(now.getDate() + END_OFFSET_DAYS);
    return { startDate: start.toISOString(), endDate: end.toISOString() };
}

/** Default filter selection using the review-page defaults. Handy for non-URL-driven consumers. */
export function defaultFilterValues(): QueryCondition {
    const { startDate, endDate } = defaultDateRange();
    return {
        referenceSubType: DEFAULT_REFERENCE_SUB_TYPE,
        caseType: DEFAULT_CASE_TYPE,
        caseSubType: DEFAULT_CASE_SUB_TYPE,
        country: resolveCountryOptions()[0]?.value || '',
        reviewStatus: DEFAULT_REVIEW_STATUS,
        reviewType: DEFAULT_REVIEW_TYPE,
        startDate,
        endDate
    };
}

/**
 * "All" defaults: every dropdown unset (empty = All / no filter), keeping the default date
 * window. Intended for use with `allowAll` (Case Audit) so the initial search is unscoped
 * except by date, and the table shows all cases until the user narrows a filter.
 */
export function allFilterValues(): QueryCondition {
    const { startDate, endDate } = defaultDateRange();
    return {
        referenceSubType: ALL_VALUE,
        caseType: ALL_VALUE,
        caseSubType: ALL_VALUE,
        country: ALL_VALUE,
        reviewStatus: ALL_VALUE,
        reviewType: ALL_VALUE,
        startDate,
        endDate
    };
}

/**
 * Case Audit page defaults: Reference SubType DP, Country = first region country (US on NA),
 * Review Status PENDING, current-week date window; the remaining dropdowns stay "All".
 */
export function caseAuditDefaultFilterValues(): QueryCondition {
    const { startDate, endDate } = defaultDateRange();
    return {
        referenceSubType: DEFAULT_REFERENCE_SUB_TYPE,
        caseType: ALL_VALUE,
        caseSubType: ALL_VALUE,
        country: resolveCountryOptions()[0]?.value || 'US',
        reviewStatus: DEFAULT_REVIEW_STATUS,
        reviewType: ALL_VALUE,
        startDate,
        endDate
    };
}

interface FilterBarProps {
    /** Current filter selection (controlled). */
    value: QueryCondition;
    /** Called with the next selection after any control changes and validation passes. */
    onChange: (next: QueryCondition) => void;
    /** Max allowed date-range span in days (default 90). */
    dateRangeLimitDays?: number;
    /** When true, prepend an "All" option (empty value) to every dropdown so a filter can be
     *  left unset. Used by the Case Audit page; the review/export page leaves this false. */
    allowAll?: boolean;
    /** Index-derived dropdown values (Case Audit: LighthouseApi.getFilterOptions). When a
     *  list is present and non-empty it replaces the static DropDownConfig options for that
     *  dropdown, and Case SubType becomes a flat, Case-Type-independent list. Omit (or a
     *  failed fetch) to fall back to the static lists. */
    dynamicOptions?: FilterOptionsPayload;
}

const FilterBar = (props: FilterBarProps) => {
    const { value, onChange } = props;
    const dateRangeLimitDays = props.dateRangeLimitDays ?? DEFAULT_DATE_RANGE_LIMIT_DAYS;
    const allowAll = props.allowAll ?? false;

    const [countryOptions] = useState<DropDownOption[]>(() => resolveCountryOptions());

    /** Maps raw index values onto dropdown options (value shown verbatim as the label). */
    const toOptions = (values: string[]): DropDownOption[] => values.map(v => ({ name: v, value: v }));
    /** Dynamic list when present and non-empty, otherwise the static fallback. */
    const orStatic = (dynamic: string[] | undefined, fallback: DropDownOption[]): DropDownOption[] =>
        (dynamic && dynamic.length > 0) ? toOptions(dynamic) : fallback;

    const dynamic = props.dynamicOptions;
    // With dynamic options Case SubType is a flat, Case-Type-independent list (deliberate:
    // no caseType↔caseSubType relation is enforced). The static fallback keeps the legacy
    // cascaded options for the currently selected Case Type.
    const usesDynamicSubTypes = !!(dynamic?.caseSubTypes && dynamic.caseSubTypes.length > 0);
    const referenceSubTypeOptions = orStatic(dynamic?.referenceSubTypes, TRANSPORTER_TYPE_OPTION);
    const caseTypeOptions = orStatic(dynamic?.caseTypes, CASE_TYPE_OPTION);
    const caseSubTypeOptions: DropDownOption[] = usesDynamicSubTypes
        ? toOptions(dynamic!.caseSubTypes)
        : (CASE_TYPE_TO_CASE_SUB_TYPE_MAP[value.caseType] || []);
    const effectiveCountryOptions = orStatic(dynamic?.countries, countryOptions);

    // When allowAll, prepend an "All" (empty-value) option so a filter can be left unset.
    const ALL_OPTION: DropDownOption = { name: 'All', value: ALL_VALUE };
    const withAll = (opts: DropDownOption[]): DropDownOption[] => allowAll ? [ALL_OPTION, ...opts] : opts;

    const referenceSubTypeRef = React.useRef<any>(null);
    const caseTypeRef = React.useRef<any>(null);
    const caseSubTypeRef = React.useRef<any>(null);
    const countryRef = React.useRef<any>(null);
    const reviewStatusRef = React.useRef<any>(null);
    const reviewTypeRef = React.useRef<any>(null);
    const dateRangeRef = React.useRef<any>(null);

    /**
     * Reads the current control values, validates the date range and applies the dependent
     * Case Type -> Case SubType reset, then emits the next value. Mirrors the original
     * ReviewExport.conditionChange behavior exactly.
     */
    const conditionChange = () => {
        const start = dateRangeRef.current.__startValue;
        const end = dateRangeRef.current.__endValue;
        const errMsg = Validator.validateDateRange(start, end, dateRangeLimitDays);
        if (errMsg) {
            alert(errMsg);
            return;
        }

        const selectedCaseType = caseTypeRef.current.__value;
        let newCaseSubType = caseSubTypeRef.current.__value;
        // Static mode only: when Case Type changes, reset Case SubType to the first option of
        // the new type ("All" when allowAll). With dynamic options Case SubType is independent.
        if (!usesDynamicSubTypes && value.caseType !== selectedCaseType) {
            const newSubTypeOptions = withAll(CASE_TYPE_TO_CASE_SUB_TYPE_MAP[selectedCaseType] || []);
            if (newSubTypeOptions.length > 0) {
                newCaseSubType = newSubTypeOptions[0].value;
            }
        }

        onChange({
            ...value,
            referenceSubType: referenceSubTypeRef.current.__value,
            caseType: selectedCaseType,
            caseSubType: newCaseSubType,
            country: countryRef.current.__value,
            reviewStatus: reviewStatusRef.current.__value,
            reviewType: reviewTypeRef.current.__value,
            startDate: start,
            endDate: end
        });
    };

    return (
        <div className='inline-display default-margin'>
            <KatDropdown placeholder={'--Select Type--'} options={withAll(referenceSubTypeOptions)}
                label={'Reference SubType'} value={value.referenceSubType}
                ref={referenceSubTypeRef} className='query-input'
                onChange={conditionChange} data-testid='reference-sub-type' />
            <KatDropdown placeholder={'--Select Type--'} options={withAll(caseTypeOptions)}
                label={'Case Type'} value={value.caseType} ref={caseTypeRef}
                data-testid='case-type' className='query-input' onChange={conditionChange} />
            <KatDropdown placeholder={'--Select Type--'} options={withAll(caseSubTypeOptions)}
                label={'Case SubType'} value={value.caseSubType} ref={caseSubTypeRef}
                data-testid='case-sub-type' className='query-input' onChange={conditionChange} />
            <KatDropdown placeholder={'--Select Country--'} options={withAll(effectiveCountryOptions)} label={'Country'}
                value={value.country} ref={countryRef} data-testid='country'
                className='query-input' onChange={conditionChange} />
            <KatDropdown placeholder={'--Select Status--'} options={withAll(STATUS_OPTION)} label={'Review Status'}
                value={value.reviewStatus} ref={reviewStatusRef} data-testid='review-status'
                className='query-input' onChange={conditionChange} />
            <KatDropdown placeholder={'--Select Type--'} options={withAll(REVIEW_TYPE_OPTION)}
                label={'ReviewType'} value={value.reviewType} ref={reviewTypeRef}
                data-testid='review-type' className='query-input' onChange={conditionChange} />
            <KatDateRangePicker startLabel={'Start Date (Required)'} startValue={value.startDate}
                endValue={value.endDate} ref={dateRangeRef}
                endLabel={'End Date (Required)'} data-testid='date-picker'
                className='date-picker' onChange={conditionChange} />
        </div>
    );
};

export default FilterBar;
